import { Injectable } from '@angular/core';
import { select } from '@ngneat/elf';
import { BaseStoreRepository } from '../base-store.repository';
import { dacInitialState, DacStateProps, dacStateStore } from './dac-state.store';
import {
  DacFormFields,
  DacPrixRequest,
  DacResponse,
  DacUpdateDto,
  DynaFields,
  FieldsDetail,
  ParamGeneralDacValue,
  ParamGeneralDacValueDto,
} from '@/types';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DacRepository extends BaseStoreRepository<DacStateProps> {
  constructor() {
    super(dacStateStore.pipe(select(state => state)), dacStateStore, dacInitialState);
  }

  addField(field: DynaFields) {
    dacStateStore.update(state => ({
      ...state,
      dto: {
        ...state.dto,
        fields: [...(state?.dto?.fields ?? []), field],
      },
    }));
  }

  removeField(fieldId: number) {
    dacStateStore.update(state => ({
      ...state,
      dto: {
        ...state.dto,
        fields: state?.dto?.fields?.filter(field => field.dynaFieldsId !== fieldId) ?? [],
      },
    }));
  }

  updateField(field: DynaFields) {
    dacStateStore.update(state => ({
      ...state,
      dto: {
        ...state.dto,
        fields: state.dto?.fields?.map(f => (f.dynaFieldsId === field.dynaFieldsId ? field : f)) ?? [],
      },
    }));
  }

  getFields(): DynaFields[] {
    return this.getState().dto?.fields ?? [];
  }

  //deprecated
  getDto(): DacStateProps['dto'] {
    return this.getState().dto;
  }

  //deprecated
  setDto(dto: Partial<DacStateProps['dto']>) {
    dacStateStore.update(state => ({
      ...state,
      dto: {
        ...state.dto,
        ...dto,
      },
    }));
  }

  setDacUpdateDto(response: DacResponse, generalParams: ParamGeneralDacValue[]) {
    const dateDebutDepot = response.dateDebutDepot?.find(date => date.status === 'ACTIF')?.date;
    const dateLimiteSoumission = response.dateLimiteSoumission?.find(date => date.status === 'ACTIF')?.date;
    const dateLimiteRecptionDemande = response.dateLimiteRecptionDemande?.find(date => date.status === 'ACTIF')?.date;
    const dateOuverture = response.dateOuverture?.find(date => date.status === 'ACTIF')?.date;
    const data = {
      conditionsParticipation: response.conditionsParticipation,
      responsableDacId: response.responsableDacId,
      payant: response.payant,
      shortlistMembers: response.etapeDac === '1' ? response?.dacShortlist?.map((company: any) => Array.isArray(company) ? company.map((c: any) => c.id) : company.id) : [],
      dacPrixRequests: response?.dacPrix?.map(dacPrix => ({
        monnaieId: dacPrix.monnaie.id,
        prix: dacPrix.prix,
      })),
      dateDebutDepot: dateDebutDepot ? new Date(dateDebutDepot) : undefined,
      dateLimiteSoumission: dateLimiteSoumission ? new Date(dateLimiteSoumission) : undefined,
      dateLimiteRecptionDemande: dateLimiteRecptionDemande ? new Date(dateLimiteRecptionDemande) : undefined,
      dateOuverture: dateOuverture ? new Date(dateOuverture) : undefined,
      parametrageGeneralDacValuesRequests: generalParams.map(param => ({
        id: param.id,
        paramGeneralDac: param.paramGeneralDac,
        dacId: param.dacId,
        dacVer: param.dacVer,
        paramGeneralValue: param.paramGeneralValue || '',
      })),
    } satisfies Partial<DacUpdateDto>;
    this.set(
      'generalParamDefinitions',
      generalParams.map(param => ({
        id: param.id,
        paramGeneralDac: param.paramGeneralDac,
        dacId: param.dacId,
        dacVer: param.dacVer,
        paramGeneralValue: param.paramGeneralValue || '',
        paramGeneralName: param.paramGeneralName,
        paramGeneralLabel: param.paramGeneralLabel,
        paramGeneralDescription: param.paramGeneralDescription,
        paramGeneralType: param.paramGeneralType,
        paramGeneralOrder: param.paramGeneralOrder,
        paramGeneralStatus: param.paramGeneralStatus,
      }))
    );
    this.set('responsableSelected', { code: response.responsableDacId });
    this.set('dacUpdateDto', data);
    this.set('initialDacUpdateDto', structuredClone(data));
    this.set('dacInfo', response);
  }

  setPendingTemplateFile(file: File | undefined) {
    dacStateStore.update(state => ({
      ...state,
      pendingTemplateFile: file,
    }));
  }

  setPendingGuideFile(file: File | undefined) {
    dacStateStore.update(state => ({
      ...state,
      pendingGuideFile: file,
    }));
  }

  getPendingTemplateFile(): File | undefined {
    return this.getState().pendingTemplateFile;
  }

  getPendingGuideFile(): File | undefined {
    return this.getState().pendingGuideFile;
  }

  //******************************************************************************* */
  addDacFormField(field: DacFormFields) {
    dacStateStore.update(state => ({
      ...state,
      dacFormFields: [...state.dacFormFields, field],
    }));
  }

  doesFieldExist(dynaFieldsId: number) {
    return this.get<DacFormFields[]>('dacFormFields').some(field => field.dynaFieldsId === dynaFieldsId);
  }

  getObservableFieldValue(dynaFieldsId: number) {
    const formFields = this.getNestedObservable<DacFormFields[]>('dacFormFields');
    return formFields.pipe(map(fields => fields.find(field => field.dynaFieldsId === dynaFieldsId)?.fieldValue));
  }

  getObservableGeneralParamValue(id?: number) {
    const generalParams = this.getNestedObservable<ParamGeneralDacValueDto[]>('dacUpdateDto', 'parametrageGeneralDacValuesRequests');
    // Matched by paramGeneralDac (the shared variable's id), not the values-row's own id: callers
    // (e.g. dac-document-form.component.ts) only have the variable id available (placement.paramGeneralDacId).
    return generalParams.pipe(map(params => params.find(param => param.paramGeneralDac === id)?.paramGeneralValue));
  }

  getObservableFieldDetails(dynaFieldsId: number): Observable<FieldsDetail[]> {
    const formFields = this.getNestedObservable<DacFormFields[]>('dacFormFields');
    return formFields.pipe(map(fields => fields.find(field => field.dynaFieldsId === dynaFieldsId)?.details ?? []));
  }

  getObservableStaticFile(documentId: number): Observable<File | null> {
    const staticFiles$ = this.getNestedObservable<Record<number, File>>('staticFiles');
    return staticFiles$.pipe(map(files => (files ? (files[documentId] ?? null) : null)));
  }

  updateFieldValue(dynaFieldsId: number, value: any) {
    dacStateStore.update(state => ({
      ...state,
      dacFormFields: state.dacFormFields.map(field => (field.dynaFieldsId === dynaFieldsId ? { ...field, fieldValue: value } : field)),
    }));
  }

  updateGeneralParamValue(id?: number, value?: any) {
    dacStateStore.update(state => ({
      ...state,
      dacUpdateDto: {
        ...state.dacUpdateDto,
        // Matched by paramGeneralDac (the shared variable's id) - see comment in getObservableGeneralParamValue.
        parametrageGeneralDacValuesRequests: state.dacUpdateDto.parametrageGeneralDacValuesRequests.map(param =>
          param.paramGeneralDac === id ? { ...param, paramGeneralValue: value } : param
        ),
      },
    }));
  }

  updateFieldDetails(dynaFieldsId: number, details: FieldsDetail[]) {
    dacStateStore.update(state => ({
      ...state,
      dacFormFields: state.dacFormFields.map(field => (field.dynaFieldsId === dynaFieldsId ? { ...field, details: [...details] } : field)),
    }));
  }

  updateStaticFile(documentId: number, file: File | null) {
    dacStateStore.update(state => ({
      ...state,
      staticFiles: {
        ...state.staticFiles,
        [documentId]: file,
      },
    }));
  }

  getUpdatePayload(dacId: number): FormData {
    const formData = new FormData();

    const dacFields = ['payant', 'responsableDacId', 'conditionsParticipation'] as const;
    const fieldAttributes = ['dacId', 'dynaFieldsId', 'dynaDocsId', 'fieldValue', 'sourceId'] as const;
    const detailAttributes = ['dynaFieldsDetailsId', 'sourceId', 'fieldValue', 'rowId'] as const;

    const appendIfNotNullish = (key: string, value: unknown) => {
      if (value !== null && value !== undefined && value !== '') {
        formData.append(key, String(value));
      }
    };

    dacFields.forEach(field => {
      const value = this.getNested('dacUpdateDto', field);
      if (value instanceof Date) {
        appendIfNotNullish(`dacWithDacDocsRequest.${field}`, value.toISOString());
      } else appendIfNotNullish(`dacWithDacDocsRequest.${field}`, value);
    });

    const shortlist: (number | number[])[] = this.getNested<(number | number[])[]>('dacUpdateDto', 'shortlistMembers');

    if (shortlist && shortlist?.length)
      shortlist.flat().forEach((companyId: number, index) => formData.append(`dacWithDacDocsRequest.shortlistMembers[${index}]`, companyId.toString()));

    const dacPrixRequests: DacPrixRequest[] = this.getNested<DacPrixRequest[]>('dacUpdateDto', 'dacPrixRequests');

    if (dacPrixRequests && dacPrixRequests?.length)
      dacPrixRequests.forEach((dacPrixRequest: DacPrixRequest, index) => {
        formData.append(`dacWithDacDocsRequest.dacPrixRequests[${index}].monnaieId`, dacPrixRequest.monnaieId.toString());
        formData.append(`dacWithDacDocsRequest.dacPrixRequests[${index}].prix`, dacPrixRequest.prix.toString());
      });

    dacStateStore.value.dacFormFields?.forEach((field, index) => {
      fieldAttributes.forEach(attr => {
        appendIfNotNullish(`dacWithDacDocsRequest.dacDocsRequests[${index}].${attr}`, (field as any)?.[attr]);
      });

      field.details?.forEach((detail, detailIndex) => {
        detailAttributes.forEach(attr => {
          appendIfNotNullish(`dacWithDacDocsRequest.dacDocsRequests[${index}].details[${detailIndex}].${attr}`, (detail as any)?.[attr]);
        });
      });
    });

    return formData;
  }
}
